/*
Copyright (C) 2018 Broadleaf Commerce

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IMetadata } from '@broadleaf/admin-components/dist/types/metadata';
import { IFulfillmentViewLocalState } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';
import { IFulfillmentContextParams } from '@broadleaf/admin-components/dist/types/oms';
import { ContextParameters } from '@broadleaf/admin-components/dist/tracking/utils/ContextRequest';
import useTracking from '@broadleaf/admin-components/dist/tracking/hooks/useTracking';
import AuthService from '@broadleaf/admin-components/dist/authentication/services/AuthService';

/**
 * Hook that generates a secure asset URL by appending an access token if necessary.
 *
 * @param {string} assetUrl The base asset URL
 * @returns {{fullAssetUrl: string}} The full asset URL with access token if applicable
 */

export type UseSecureAssetUrlParams = {
  fullAssetUrl: string;
};

export default function useSecureAssetUrl(
  assetUrl: string
): UseSecureAssetUrlParams {
  const [assetToken, setAssetToken] = useState<{
    access_token: string;
    expiry?: number;
  }>(undefined);
  const refreshAssetToken = useCallback(async () => {
    if (
      !assetUrl ||
      !(
        assetUrl.includes('/secure/') || assetUrl.includes('/tax-documentation')
      )
    ) {
      return;
    }
    const token = (await AuthService.getToken('ASSET')) as {
      access_token: string;
      expiry?: number;
    };
    setAssetToken(token);
    setTimeout(() => refreshAssetToken(), token.expiry - Date.now() - 10000);
  }, [assetUrl, setAssetToken]);

  useEffect(() => {
    refreshAssetToken();
  }, [assetUrl, refreshAssetToken]);

  const fullAssetUrl = useMemo(() => {
    if (assetUrl && assetToken) {
      // add the access token to the src url, useing & or ? as appropriate
      const separator = assetUrl.includes('?') ? '&' : '?';
      return `${assetUrl}${separator}access_token=${assetToken.access_token}`;
    }
    return assetUrl;
  }, [assetUrl, assetToken]);
  return { fullAssetUrl };
}
